import {
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { errorToast, successToast } from "../../helpers";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import { useAddNotificationMutation } from "../../services/notification";
import {
  useAddSettingsMutation,
  useLazyGetSettingsByIdQuery,
  useUpdateSettingsMutation,
} from "../../services/settings";
import { useLazyAllCategoryQuery } from "../../services/main";
import { isNumber } from "../../utils/validation";
import Loader from "../../helpers/constants/Loader";
import useTranslation from "../../hooks/Translation";

const AddCommision = () => {
  const { id } = useParams();
  const t = useTranslation();
  const navigate = useNavigate();
  const [addSetting] = useAddSettingsMutation();
  const [getCategory] = useLazyAllCategoryQuery();
  const [updateSetting] = useUpdateSettingsMutation();
  const [settingById] = useLazyGetSettingsByIdQuery();
  const [selectedCategory, setSelectedCategory] = useState("");
  const [dropdownType, setDropdownType] = useState("");
  const [categoryData, setCategoryData] = useState([]);
  const [selectType, setSelectType] = useState("");
  const [settingData, setSettingData] = useState<any>();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePricingChange = (event: SelectChangeEvent) => {
    setSelectedCategory(event.target.value as string);
  };

  const handleDropdownTypeChange = (event: SelectChangeEvent) => {
    setSelectType(event.target.value as string);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      tax_flat: settingData?.tax_flat || "",
      tax_percentage: settingData?.tax_percentage || "",
      platform_commission_flat: settingData?.platform_commission_flat || "",
      platform_commission_percentage:
        settingData?.platform_commission_percentage || "",
    },
    validationSchema: Yup.object({
      tax_flat:
        selectType === "1"
          ? Yup.string().required(t.error.required)
          : Yup.string().notRequired(),

      tax_percentage:
        selectType === "2"
          ? Yup.string().required(t.error.required)
          : Yup.string().notRequired(),

      platform_commission_flat:
        selectType === "1"
          ? Yup.string().required(t.error.required)
          : Yup.string().notRequired(),
      platform_commission_percentage:
        selectType === "2"
          ? Yup.string().required(t.error.required)
          : Yup.string().notRequired(),
    }),
    onSubmit: async (values) => {
      let body: any = {
        category: selectedCategory,
        tax_flat: Number(values.tax_flat),
        tax_percentage: Number(values.tax_percentage),
        platform_commission_flat: Number(values.platform_commission_flat),
        platform_commission_percentage: Number(
          values.platform_commission_percentage
        ),
        platform_commission_type: Number(selectType),
      };

      if (!id) {
        try {
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await addSetting(body).unwrap();
          if (response.status === 200) {
            successToast(t.main.settingsAdded);
            navigate("/settings");
          } else {
            errorToast(response.message);
          }
        } catch (e: any) {
          console.error("API call failed", e);
          errorToast(e?.data?.message);
        }
      } else {
        try {
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateSetting({ body, id }).unwrap();
          if (response.status === 200) {
            successToast(t.main.settingsUpdated);
            navigate("/settings");
          } else {
            errorToast(response.message);
          }
        } catch (e: any) {
          console.error("API call failed", e);
          errorToast(e?.data?.message);
        }
      }
    },
  });

  const getCategoryDetail = async () => {
    try {
      const res = await getCategory({}).unwrap();
      if (res?.status === 200) {
        setCategoryData(res?.data);
      }
    } catch (error: any) {
      console.log(error, "errror");
      errorToast(error?.data?.message || "");
    }
  };

  const getSettingsById = async () => {
    setLoading(true);
    try {
      const response = await settingById({ id }).unwrap();
      if (response?.status === 200) {
        setLoading(false);
        setSettingData(response?.data);
        setSelectType(String(response?.data?.platform_commission_type));
        setSelectedCategory(response?.data?.category?.id);
      }
    } catch (error: any) {
      setLoading(false);
      console.log(error, "errror");
      errorToast(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getCategoryDetail();
  }, []);

  useEffect(() => {
    if (id) {
      getSettingsById();
    }
  }, []);

  return (
    <>
      <div>
        <Loader isLoad={loading} />
        <div className="main_loyout">
          <div className="dashboard">
            <h1 className="mn_hdng">{t.main.addSettings}</h1>
            <Button
              className="btn btn_primary"
              onClick={() => {
                navigate("/settings");
              }}
            >
              {t.main.back}
            </Button>
          </div>
          <Card className="cards">
            <form onSubmit={formik.handleSubmit}>
              <CardContent sx={{ p: 1 }}>
                <Grid container spacing={2}>
                  <Grid item lg={6} md={4} sm={6} xs={12}>
                    <Typography className="custom_label">
                      {t.main.category}
                    </Typography>
                    <FormControl sx={{ width: "100%" }}>
                      <Select
                        className="select_div"
                        fullWidth
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        // value={priceType}
                        value={selectedCategory}
                        inputProps={{
                          "aria-label": "Without label",
                        }}
                        displayEmpty
                        onChange={handlePricingChange}
                        error={error && !selectedCategory}
                      >
                        <MenuItem value="" disabled>
                          {t.main.selectCategory}
                        </MenuItem>
                        {categoryData?.length
                          ? categoryData?.map((item: any) => (
                              <MenuItem key={item?.id} value={item?.id}>
                                {item?.category_name || ""}
                              </MenuItem>
                            ))
                          : null}
                      </Select>
                      {error && !selectedCategory ? (
                        <h6 className="err_msg">{t.error.required}</h6>
                      ) : (
                        ""
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <Typography className="custom_label">
                      {t.main.selectType}
                    </Typography>
                    <FormControl sx={{ width: "100%" }}>
                      <Select
                        value={selectType}
                        onChange={handleDropdownTypeChange}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          {t.main.selectType}
                        </MenuItem>
                        <MenuItem value="1">Flat</MenuItem>
                        <MenuItem value="2">Percentage</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {selectType === "1" ? (
                    <Grid item lg={6} md={6} sm={6} xs={12}>
                      <Typography className="custom_label">
                        {t.main.tax}
                      </Typography>
                      <TextField
                        hiddenLabel
                        type="text"
                        name="tax_flat"
                        variant="outlined"
                        fullWidth
                        inputProps={{ maxLength: 5 }}
                        placeholder={t.main.tax}
                        value={formik.values.tax_flat}
                        onChange={(val) => {
                          const value = val.target.value;
                          if (value === " " || value === ".") return;

                          // Regex to allow only numbers with up to two decimal places
                          if (/^\d*\.?\d{0,2}$/.test(value)) {
                            formik?.handleChange(val);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.tax_flat &&
                          Boolean(formik.errors.tax_flat)
                        }
                        helperText={
                          formik.touched.tax_flat && formik.errors.tax_flat
                            ? formik.errors.tax_flat.toString()
                            : ""
                        }
                      />
                    </Grid>
                  ) : (
                    <Grid item lg={6} md={6} sm={6} xs={12}>
                      <Typography className="custom_label">
                        {t.main.taxPer}
                      </Typography>
                      <TextField
                        hiddenLabel
                        type="text"
                        name="tax_percentage"
                        variant="outlined"
                        fullWidth
                        placeholder={t.main.taxPer}
                        value={formik.values.tax_percentage}
                        onChange={(val) => {
                          const value = val.target.value;
                          if (value === " " || value === ".") return;

                          // Regex to allow only numbers with up to two decimal places
                          if (/^\d*\.?\d{0,2}$/.test(value)) {
                            formik?.handleChange(val);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        inputProps={{ maxLength: 3 }}
                        error={
                          formik.touched.tax_percentage &&
                          Boolean(formik.errors.tax_percentage)
                        }
                        helperText={
                          formik.touched.tax_percentage &&
                          formik.errors.tax_percentage
                            ? formik.errors.tax_percentage.toString()
                            : ""
                        }
                      />
                    </Grid>
                  )}

                  {selectType === "1" ? (
                    <Grid item lg={6} md={6} sm={6} xs={12}>
                      <Typography className="custom_label">
                        {t.main.platformCommission}
                      </Typography>
                      <TextField
                        hiddenLabel
                        type="text"
                        name="platform_commission_flat"
                        variant="outlined"
                        fullWidth
                        placeholder={t.main.platformCommission}
                        value={formik.values.platform_commission_flat}
                        // onChange={(val) => {
                        //   if (
                        //     val.target.value === " " ||
                        //     val.target.value === "."
                        //   ) {
                        //   } else if (isNumber(val.target.value)) {
                        //     formik.handleChange(val);
                        //   }
                        // }}
                        onChange={(val) => {
                          const value = val.target.value;
                          if (value === " " || value === ".") return;

                          // Regex to allow only numbers with up to two decimal places
                          if (/^\d*\.?\d{0,2}$/.test(value)) {
                            formik?.handleChange(val);
                          }
                        }}
                        inputProps={{ maxLength: 5 }}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.platform_commission_flat &&
                          Boolean(formik.errors.platform_commission_flat)
                        }
                        helperText={
                          formik.touched.platform_commission_flat &&
                          formik.errors.platform_commission_flat
                            ? formik.errors.platform_commission_flat.toString()
                            : ""
                        }
                      />
                    </Grid>
                  ) : (
                    <Grid item lg={6} md={6} sm={6} xs={12}>
                      <Typography className="custom_label">
                        {t.main.commissionPer}
                      </Typography>
                      <TextField
                        hiddenLabel
                        type="text"
                        name="platform_commission_percentage"
                        variant="outlined"
                        fullWidth
                        placeholder={t.main.commissionPer}
                        inputProps={{ maxLength: 3 }}
                        value={formik.values.platform_commission_percentage}
                        onChange={(val) => {
                          const value = val.target.value;
                          if (value === " " || value === ".") return;

                          // Regex to allow only numbers with up to two decimal places
                          if (/^\d*\.?\d{0,2}$/.test(value)) {
                            formik?.handleChange(val);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.platform_commission_percentage &&
                          Boolean(formik.errors.platform_commission_percentage)
                        }
                        helperText={
                          formik.touched.platform_commission_percentage &&
                          formik.errors.platform_commission_percentage
                            ? formik.errors.platform_commission_percentage.toString()
                            : ""
                        }
                      />
                    </Grid>
                  )}
                </Grid>
                <div className="form_btn">
                  <Button
                    size="large"
                    type="submit"
                    className="btn btn_primary"
                  >
                    {t.main.save}
                  </Button>
                </div>
              </CardContent>
            </form>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AddCommision;
